/**
 * Accrual Vendors — manage which suppliers are re-dated from payment date to invoice date
 * on the accrual P&L.
 *
 * This used to be a hardcoded array in the backend (adding a vendor meant a code change and
 * a deploy). Two things it now gets right:
 *
 *  - PAYEE ALIASES. A supplier is often paid under a different name. Wavefront is owned by
 *    Philip Nguyen and its payments are memo'd "Philip Nguyen", so matching only the string
 *    "wavefront" never suppressed the payment — the invoice was added on top of it and
 *    Marketing Services double-counted Wavefront every month. Aliases fix that.
 *
 *  - COVERAGE. Substitution only applies in months where the supplier actually has an
 *    invoice. A month with no invoice keeps its payment entry, so adding a vendor with
 *    patchy invoice coverage can never silently delete expense. The dialog shows the
 *    coverage so you can see this before saving.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip,
  Alert, Tooltip, CircularProgress, Stack, Switch, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import financialConfigService from '../../services/financialConfigService';

const EMPTY = {
  id: null,
  vendor_label: '',
  supplier_pattern: '',
  memo_patterns: '',
  account_code: '',
  notes: '',
  is_active: 1,
};

export default function AccrualVendorsManager() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await financialConfigService.getAccrualVendors();
      setVendors(res?.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load accrual vendors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setCoverage(null); setError(''); setDialogOpen(true); };

  const openEdit = (v) => {
    setForm({
      id: v.id,
      vendor_label: v.vendor_label || '',
      supplier_pattern: v.supplier_pattern || '',
      memo_patterns: (v.memo_patterns || []).join(', '),
      account_code: v.account_code || '',
      notes: v.notes || '',
      is_active: Number(v.is_active) === 0 ? 0 : 1,
    });
    setCoverage(null);
    setError(''); setDialogOpen(true);
    if (v.supplier_pattern) checkCoverage(v.supplier_pattern);
  };

  const checkCoverage = async (pattern) => {
    const p = String(pattern || '').trim();
    if (!p) { setCoverage(null); return; }
    setCoverageLoading(true);
    try {
      const res = await financialConfigService.getAccrualVendorCoverage(p);
      setCoverage(res?.data || []);
    } catch {
      setCoverage(null);
    } finally {
      setCoverageLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await financialConfigService.saveAccrualVendor({
        ...form,
        memo_patterns: String(form.memo_patterns || '')
          .split(',').map(s => s.trim()).filter(Boolean),
      });
      setDialogOpen(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Remove "${v.vendor_label}" from accrual substitution?\n\nIts invoices will stop replacing its payment entries — the P&L reverts to payment-date (cash) timing for this vendor. Reversible.`)) return;
    try {
      await financialConfigService.deleteAccrualVendor(v.id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to remove');
    }
  };

  const monthsCovered = Array.isArray(coverage) ? coverage.length : null;

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="h6">Accrual Vendors</Typography>
          <Typography variant="body2" color="text.secondary">
            Suppliers whose spend is shown on its <strong>invoice date</strong> instead of the date
            the card or bank was charged. Everything else stays payment-timed.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Add vendor
        </Button>
      </Box>

      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
        Substitution only applies in months where the supplier <strong>actually has an invoice</strong>.
        A month with no invoice keeps its payment entry, so adding a vendor can never silently
        delete expense. Add <strong>payee aliases</strong> when the bank/card payee differs from the
        supplier name (e.g. Wavefront is paid as “Philip Nguyen”) — without the alias the payment
        is not suppressed and the invoice gets counted on top of it.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vendor</TableCell>
                <TableCell>Supplier match</TableCell>
                <TableCell>Payee aliases</TableCell>
                <TableCell>GL account</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vendors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No accrual vendors configured — the P&amp;L is entirely payment-timed.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {vendors.map(v => (
                <TableRow key={v.id} sx={{ opacity: Number(v.is_active) === 0 ? 0.45 : 1 }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{v.vendor_label}</Typography>
                    {v.notes && (
                      <Typography variant="caption" color="text.secondary">{v.notes}</Typography>
                    )}
                  </TableCell>
                  <TableCell><code>{v.supplier_pattern}</code></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(v.memo_patterns || []).map(p => (
                        <Chip key={p} label={p} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>{v.account_code}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={Number(v.is_active) === 0 ? 'Inactive' : 'Active'}
                      color={Number(v.is_active) === 0 ? 'default' : 'success'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(v)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    {Number(v.is_active) !== 0 && (
                      <Tooltip title="Remove from accrual substitution">
                        <IconButton size="small" onClick={() => remove(v)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? 'Edit accrual vendor' : 'Add accrual vendor'}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Vendor name" fullWidth value={form.vendor_label}
              onChange={e => setForm({ ...form, vendor_label: e.target.value })}
              helperText="Shown in this list only."
            />
            <TextField
              label="Supplier match" fullWidth value={form.supplier_pattern}
              onChange={e => setForm({ ...form, supplier_pattern: e.target.value })}
              onBlur={e => checkCoverage(e.target.value)}
              helperText="Matched against the supplier name on invoices (contains, case-insensitive). e.g. wavefront"
            />
            <TextField
              label="Payee aliases" fullWidth value={form.memo_patterns}
              onChange={e => setForm({ ...form, memo_patterns: e.target.value })}
              helperText="Comma-separated. Payment entries whose memo contains ANY of these are replaced by the invoice. Add the payee name if it differs from the supplier — e.g. wavefront, philip, nguyen. Defaults to the supplier match."
            />
            <TextField
              label="GL account code" fullWidth value={form.account_code}
              onChange={e => setForm({ ...form, account_code: e.target.value })}
              helperText="4-digit code the invoice books to, e.g. 8455 Marketing Services & PR. Must be an active GL account."
            />
            <TextField
              label="Notes" fullWidth multiline minRows={2} value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Number(form.is_active) !== 0}
                  onChange={e => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                />
              }
              label="Active"
            />

            {coverageLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} /> <Typography variant="caption">Checking invoice coverage…</Typography>
              </Box>
            )}
            {!coverageLoading && coverage !== null && (
              monthsCovered === 0 ? (
                <Alert severity="warning">
                  <strong>No invoices found</strong> for “{form.supplier_pattern}”. Nothing will be
                  substituted — this vendor stays payment-timed until invoices land. Safe to save,
                  but it won’t change the P&amp;L yet.
                </Alert>
              ) : (
                <Alert severity={monthsCovered < 3 ? 'warning' : 'success'}>
                  Invoices found in <strong>{monthsCovered}</strong> month{monthsCovered === 1 ? '' : 's'}
                  {coverage[0]?.month ? ` (latest ${String(coverage[0].month).slice(0, 7)})` : ''}.
                  {monthsCovered < 3 && ' Thin coverage — the other months keep their payment entries.'}
                </Alert>
              )
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
